import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { PropositionDetail } from '../components/commercial/PropositionDetail';

import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ParcoursCommercial } from '../components/commercial/ParcoursCommercial';
import { usePerimetre } from '../site/usePerimetre';
import { useTaillePage } from '../lib/useTaillePage';
import {
  useLancerCalcul,
  useMatching,
  usePositionner,
  type LigneImmersion,
  type LigneMatching,
  type PropositionVue,
  type SaisiePositionnement,
} from '../lib/useMatching';
import {
  immersionATraiter,
  libelleImmersion,
  libellePositionnement,
} from '../lib/immersion-statut';
import { SuggestionsMatching } from '../components/commercial/SuggestionsMatching';
import { HistoriqueMatching } from '../components/commercial/HistoriqueMatching';
import {
  appliquerFiltres,
  FiltresCroisement,
  FILTRES_VIDES,
  LIBELLES_STATUT_PROPOSITION,
  nombreFiltresActifs,
  type EtatFiltresCroisement,
} from '../components/commercial/FiltresCroisement';
import { ResumeCriteres } from '../components/commercial/ResumeCriteres';
import { PositionnementManuel } from '../components/commercial/PositionnementManuel';
import { ErreurApi } from '../lib/api';
import { useToast } from '../components/ui/useToast';
import { useAuth } from '../auth/useAuth';

type Onglet = 'croisement' | 'suggestions' | 'historique';

const ONGLETS: { cle: Onglet; libelle: string }[] = [
  { cle: 'croisement', libelle: 'Croisement' },
  { cle: 'suggestions', libelle: 'Suggestions classées' },
  { cle: 'historique', libelle: 'Historique' },
];

function jour(valeur: string | null): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * Écran « Matching entreprise » du pôle commercial (CDC §5.3).
 *
 * Les candidats à positionner sont croisés avec les besoins ouverts du
 * répertoire. L'adéquation est une somme pondérée de critères connus — métier,
 * formation, mobilité, disponibilité — et le détail est visible : le chargé de
 * relation entreprise doit pouvoir contredire le classement.
 */
export function ComMatchingPage(): JSX.Element {
  const navigate = useNavigate();
  const { site } = usePerimetre();
  const { data, isLoading, isFetching, error, refetch } = useMatching(site);
  const positionner = usePositionner();
  const calcul = useLancerCalcul();
  const toast = useToast();
  const { peutAcceder } = useAuth();

  const compteurs = data?.compteurs;
  const candidats = useMemo(() => data?.candidats ?? [], [data?.candidats]);

  const [onglet, setOnglet] = useState<Onglet>('croisement');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  const [filtres, setFiltres] = useState<EtatFiltresCroisement>(FILTRES_VIDES);
  const [tri, setTri] = useState<'score' | 'candidat'>('score');

  /** Positionnement manuel ouvert, avec le candidat déjà choisi s'il vient d'une ligne. */
  const [manuel, setManuel] = useState<{ apprenantId: string | null } | null>(null);
  const panneauManuel = useRef<HTMLDivElement>(null);

  const ouvrirManuel = (apprenantId: string | null): void => {
    setOnglet('croisement');
    setManuel({ apprenantId });
    requestAnimationFrame(() =>
      panneauManuel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
    );
  };

  useEffect(() => {
    setPage(1);
  }, [site, taillePage]);

  // Un filtre qui change sans remettre la pagination à zéro laisse l'écran sur
  // une page qui n'existe plus, et donne « aucun résultat » à tort.
  useEffect(() => {
    setPage(1);
  }, [filtres]);

  /**
   * Lignes retenues, avec les propositions qui passent les filtres.
   *
   * Le tri se fait sur le score **rendu par l'API** : l'écran n'en recalcule
   * aucun, il ne fait que retenir et ordonner ce qu'il a reçu.
   */
  const candidatsFiltres = useMemo(() => {
    const retenus = appliquerFiltres(candidats, filtres);

    if (tri === 'score') {
      // `score` et `scoreNormalise` portent la même valeur côté API ; on lit le
      // second en repli pour les propositions enregistrées avant que le premier
      // n'existe, plutôt que de les renvoyer toutes en bas de liste.
      const meilleur = (entree: (typeof retenus)[number]): number =>
        entree.propositions[0]?.score ?? entree.propositions[0]?.scoreNormalise ?? -1;
      retenus.sort((a, b) => meilleur(b) - meilleur(a));
    } else {
      retenus.sort((a, b) => a.ligne.candidat.localeCompare(b.ligne.candidat, 'fr'));
    }

    return retenus;
  }, [candidats, filtres, tri]);

  const filtresActifs = nombreFiltresActifs(filtres);

  const totalCandidats = candidatsFiltres.length;
  const pagesCandidats = Math.max(1, Math.ceil(totalCandidats / taillePage));
  
  const candidatsAffiches = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return candidatsFiltres.slice(debut, debut + taillePage);
  }, [candidatsFiltres, page, taillePage]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Matching entreprise"
        sousTitre="Candidats croisés avec les entreprises disponibles"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <BoutonSecondaire
              onClick={() => (manuel ? setManuel(null) : ouvrirManuel(null))}
            >
              Positionnement manuel
            </BoutonSecondaire>
            <BoutonPrincipal
              onClick={() =>
                calcul.mutate(
                  { site },
                  {
                    onSuccess: (resultat) => {
                      void refetch();
                      // Le calcul lancé ici est toujours « à la demande » : il
                      // s'exécute même quand le passage automatique est coupé.
                      toast(
                        `${resultat.propositionsCreees} nouvelle(s) proposition(s), ` +
                          `${resultat.propositionsActualisees} actualisée(s), ` +
                          `${resultat.notifications} notification(s)`,
                      );
                    },
                  },
                )
              }
              disabled={isFetching || calcul.isPending}
            >
              Relancer le calcul
            </BoutonPrincipal>
          </div>
        }
      />

      <ParcoursCommercial etape="matching" />

      <BarreSites />

      {/* Onglets : cibles tactiles pleine hauteur, en colonne sous 640 px pour
          rester utilisables au doigt sur tablette comme au téléphone. */}
      <nav
        className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-card"
        aria-label="Vues du matching"
      >
        {ONGLETS.map((entree) => (
          <button
            key={entree.cle}
            type="button"
            onClick={() => setOnglet(entree.cle)}
            aria-current={onglet === entree.cle ? 'page' : undefined}
            className={`min-h-[44px] min-w-0 flex-1 rounded-xl px-4 text-sm font-semibold transition sm:flex-none ${
              onglet === entree.cle
                ? 'bg-codialis-navy text-white'
                : 'text-codialis-gray hover:bg-gray-50 hover:text-codialis-navy'
            }`}
          >
            {entree.libelle}
          </button>
        ))}
      </nav>

      <div className="grid grid-cols-1 items-center gap-5 rounded-2xl bg-gradient-to-br from-codialis-navy to-codialis-blue p-4 sm:p-6 text-white lg:grid-cols-4">
        <div className="lg:col-span-3 min-w-0">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-mint">
            Matching entreprise
          </div>
          <h2 className="font-display mb-1 text-xl font-bold">
            Chaque candidat croisé avec les besoins ouverts des entreprises
          </h2>
          <p className="text-sm text-white/70">
            Le dossier de l'apprenant est comparé au tableau des entreprises disponibles :
            métier, formation, mobilité et disponibilité. Le détail de chaque score reste
            visible, pour pouvoir contredire le classement.
          </p>
        </div>
        <div className="text-center">
          <div className="font-display text-4xl font-extrabold text-codialis-mint">
            {compteurs?.postesOuverts ?? 0}
          </div>
          <div className="mt-1 text-xs text-white/60">poste(s) ouvert(s) à croiser</div>
        </div>
      </div>

      {positionner.error instanceof ErreurApi && (
        <MessageErreur message={positionner.error.message || 'Action refusée.'} />
      )}



      {onglet === 'suggestions' && <SuggestionsMatching site={site} />}
      {onglet === 'historique' && <HistoriqueMatching site={site} />}

      {onglet === 'croisement' && (
        <>
          {manuel && (
            <div ref={panneauManuel} className="scroll-mt-24">
              <PositionnementManuel
                key={manuel.apprenantId ?? ''}
                candidats={candidats}
                apprenantIdInitial={manuel.apprenantId}
                peutCreerEntreprise={peutAcceder('com-entreprises')}
                onFermer={() => setManuel(null)}
              />
            </div>
          )}

          <Carte titre="Croisement candidats × entreprises disponibles" sansBordure>
            {error && (
              <div className="p-4 sm:p-6">
                <MessageErreur
                  message={
                    error instanceof ErreurApi
                      ? error.message
                      : 'Le matching n’a pas pu être calculé.'
                  }
                />
              </div>
            )}
            {isLoading && <Chargement libelle="Calcul du matching…" />}

            
            {!isLoading && !error && (
              <FiltresCroisement
                filtres={filtres}
                onFiltres={setFiltres}
                lignes={candidats}
                tri={tri}
                onTri={setTri}
              />
            )}

            {!isLoading && !error && (
              <div className="relative overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                    <tr>
                      {[
                        'Candidat',
                        'Formation',
                        'Entreprise proposée',
                        'Adéquation',
                        '',
                      ].map((colonne, index) => (
                        <th key={colonne || index} className="px-4 py-3 text-left font-bold">
                          {colonne || <span className="sr-only">Actions</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {totalCandidats > 0 ? (
                      candidatsAffiches.map(({ ligne, propositions }) => (
                        <LigneCroisement
                          key={ligne.apprenantId}
                          ligne={ligne}
                          propositions={propositions}
                          libelles={data?.libelles}
                          enCours={positionner.isPending}
                          onAutreEntreprise={() => ouvrirManuel(ligne.apprenantId)}
                          onPositionner={(proposition, saisie) =>
                            positionner.mutate(
                              {
                                ...saisie,
                                apprenantId: ligne.apprenantId,
                                entrepriseId: proposition.entrepriseId,
                                besoinId: proposition.besoinId,
                              },
                              { onSuccess: ({ message }) => toast(message) },
                            )
                          }
                        />
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-10 text-center text-sm text-codialis-gray"
                        >
                          {filtresActifs > 0 ? (
                            <>
                              Aucun candidat ne correspond aux filtres.{' '}
                              <button
                                type="button"
                                onClick={() => setFiltres(FILTRES_VIDES)}
                                className="font-semibold text-codialis-sky hover:underline"
                              >
                                Réinitialiser les filtres
                              </button>
                            </>
                          ) : (
                            'Aucun candidat à positionner sur ce périmètre.'
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {!isLoading && !error && totalCandidats > 0 && (
              <Pagination
                page={page}
                pages={pagesCandidats}
                total={totalCandidats}
                affiches={candidatsAffiches.length}
                taillePage={taillePage}
                onPage={setPage}
                onChangerTaille={setTaillePage}
                uniteLabel="candidat"
              />
            )}
          </Carte>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Carte titre="Période d'immersion" sansBordure>
              <div className="space-y-3 p-4 sm:p-6">
                {data?.immersions && data.immersions.length > 0 ? (
                  <>
                    {/* La carte est un résumé de quatre lignes : ce sont celles
                        qui réclament une action qui montent — une immersion
                        terminée sans compte rendu d'entretien d'abord. */}
                    {[...data.immersions]
                      .sort(
                        (a, b) =>
                          Number(immersionATraiter(b)) - Number(immersionATraiter(a)),
                      )
                      .slice(0, 4)
                      .map((immersion) => (
                        <LigneImmersionSuivi key={immersion.id} immersion={immersion} />
                      ))}
                    {data.immersions.length > 4 && (
                      <button
                        type="button"
                        onClick={() => navigate('/periode-immersion')}
                        className="w-full bg-white border border-gray-200 hover:bg-gray-50 text-codialis-navy px-4 py-2.5 rounded-xl text-sm font-semibold transition mt-4"
                      >
                        Voir plus
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-codialis-gray">Aucune immersion sur ce périmètre.</p>
                )}
                <div className="text-[11px] text-codialis-gray mt-2">
                  Immersion validée par l'entreprise → bascule automatique vers le circuit
                  administratif (contrat d'apprentissage).
                </div>
              </div>
            </Carte>

            <Carte titre="Diffusion d'offres" sansBordure>
              <div className="space-y-3 p-4 sm:p-6">
                <p className="text-sm text-codialis-gray">
                  {compteurs?.postesOuverts ?? 0} poste(s) ouvert(s) pour{' '}
                  {compteurs?.aPositionner ?? 0} candidat(s) à positionner. Cibler la diffusion
                  depuis le filtre du pôle pédagogique.
                </p>
                {peutAcceder('ped-sanscontrat') && (
                  <button
                    type="button"
                    onClick={() => navigate('/ped-sanscontrat')}
                    className="w-full bg-white border border-gray-200 hover:bg-gray-50 text-codialis-navy px-4 py-2.5 rounded-xl text-sm font-semibold transition"
                  >
                    Voir les apprenants sans entreprise
                  </button>
                )}
              </div>
            </Carte>
          </div>
        </>
      )}

    </div>
  );
}

function LigneCroisement({
  ligne,
  propositions,
  libelles,
  enCours,
  onAutreEntreprise,
  onPositionner,
}: {
  ligne: LigneMatching;
  /** Propositions retenues par les filtres, dans l'ordre reçu de l'API. */
  propositions: PropositionVue[];
  libelles?: Record<string, string>;
  enCours: boolean;
  /** Positionnement manuel de ce candidat, hors des suggestions. */
  onAutreEntreprise: () => void;
  onPositionner: (proposition: PropositionVue, saisie: SaisiePositionnement) => void;
}): JSX.Element {
  const [deplie, setDeplie] = useState(false);
  const avecSuggestions = propositions.length > 0 && !ligne.immersion;

  const meilleure = propositions[0] ?? null;
  const statut = meilleure?.statutEnregistre
    ? LIBELLES_STATUT_PROPOSITION[meilleure.statutEnregistre]
    : null;

  return (
    <>
      <tr className="transition hover:bg-gray-50">
        <td className="px-4 py-3">
          <Link
            to={`/adm-dossier/${ligne.apprenantId}`}
            className="font-semibold text-codialis-navy hover:text-codialis-sky"
          >
            {ligne.candidat}
          </Link>
          <div className="text-[11px] text-codialis-gray">
            {ligne.siteCode}
            {ligne.ville ? ` · ${ligne.ville}` : ''}
            {ligne.rqth ? ' · RQTH' : ''}
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-codialis-gray">{ligne.formation ?? '—'}</td>
        <td className="px-4 py-3">
          {ligne.immersion ? (
            <span className="text-xs text-codialis-navy">
              {ligne.immersion.entreprise}
              <Pastille
                texte={libellePositionnement(ligne.immersion).texte}
                ton={libellePositionnement(ligne.immersion).ton}
              />
            </span>
          ) : meilleure ? (
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-codialis-navy">
                  {meilleure.entrepriseNom}
                </span>
                <PastilleSite site={ligne.siteCode} />
                {statut && <Pastille texte={statut.texte} ton={statut.ton} />}
              </div>
              {meilleure.entrepriseVille && (
                <div className="text-[11px] text-codialis-gray">{meilleure.entrepriseVille}</div>
              )}
            </div>
          ) : (
            <span className="text-xs text-codialis-gray">Aucun besoin ouvert dans l’antenne</span>
          )}
        </td>
        <td className="px-4 py-3">
          {meilleure && !ligne.immersion ? (
            <div className="min-w-[200px] space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="progress-track h-1.5 w-16 overflow-hidden rounded-full">
                  <div className="progress-bar h-full" style={{ width: `${meilleure.score}%` }} />
                </div>
                <span className="text-xs font-bold text-codialis-navy">{meilleure.score} %</span>
                {meilleure.couverture && (
                  <span className="text-[10px] text-codialis-gray">
                    {meilleure.couverture.mesures}/{meilleure.couverture.retenus} critères
                  </span>
                )}
              </div>
              <ResumeCriteres adequation={meilleure} libelles={libelles} />
            </div>
          ) : (
            <span className="text-xs text-codialis-gray">—</span>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Sans suggestion, le candidat se positionne quand même : le
                bouton mène au positionnement manuel, l'API dit s'il le peut. */}
            <button
              type="button"
              onClick={avecSuggestions ? () => setDeplie((valeur) => !valeur) : onAutreEntreprise}
              className="whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
            >
              {avecSuggestions && deplie ? 'Fermer' : 'Positionner'}
            </button>
          </div>
        </td>
      </tr>

      {avecSuggestions && deplie && (
        <tr className="bg-gray-50">
          <td colSpan={5} className="px-4 sm:px-6 py-4">
            <div className="space-y-4">
              {propositions.map((proposition) => (
                <PropositionDetail 
                  key={proposition.besoinId} 
                  proposition={proposition}
                  onPositionner={(saisie) => onPositionner(proposition, saisie)}
                  enCours={enCours}
                />
              ))}
              <button
                type="button"
                onClick={onAutreEntreprise}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-dashed border-codialis-sky/40 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                <Building2 className="h-4 w-4" aria-hidden />
                Choisir une autre entreprise
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function LigneImmersionSuivi({
  immersion,
}: {
  immersion: LigneImmersion;
}): JSX.Element {
  const libelle = libelleImmersion(immersion);

  return (
    <div className="px-4 sm:px-6 py-3 border-b border-gray-100 last:border-0">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-codialis-navy">
            {immersion.candidat} → {immersion.entreprise}
          </div>
          <div className="text-[11px] text-codialis-gray">
            {jour(immersion.dateDebut)} → {jour(immersion.dateFin)}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <Pastille texte={libelle.texte} ton={libelle.ton} />
          {immersionATraiter(immersion) && <Pastille texte="À traiter" ton="danger" />}
        </div>
      </div>
    </div>
  );
}
